import { useState, useEffect } from "react";
import axios from "axios";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { AlertTriangle, TrendingUp, CheckCircle, XCircle, Clock, BarChart3, RefreshCw } from "lucide-react";
import { useCluster } from "@/components/layout/Layout";
import { cn } from "@/lib/utils";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const statusStyle = {
  Running: "text-emerald-400 bg-emerald-400/10 border-emerald-400/20",
  Warning: "text-amber-400 bg-amber-400/10 border-amber-400/20",
  CrashLoopBackOff: "text-rose-400 bg-rose-400/10 border-rose-400/20",
  Pending: "text-blue-400 bg-blue-400/10 border-blue-400/20",
};

function SummaryCard({ label, value, color, icon: Icon }) {
  return (
    <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-semibold uppercase tracking-widest text-slate-500">{label}</span>
        <Icon size={13} className={color} />
      </div>
      <p className="text-2xl font-semibold" style={{ fontFamily: "'IBM Plex Sans', sans-serif", color: "var(--tw-text-opacity)" }}>
        <span className={color}>{value}</span>
      </p>
    </div>
  );
}

export default function Analytics() {
  const { clusterId } = useCluster();
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/clusters/${clusterId}/workload-analytics`)
      .then((r) => { setAnalytics(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, [clusterId]);

  if (loading || !analytics) {
    return (
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        {[...Array(8)].map((_, i) => <div key={i} className="h-24 bg-slate-800/50 rounded-md animate-pulse" />)}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-50" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Workload Analytics</h2>
        <button onClick={() => window.location.reload()} className="p-1.5 rounded-md border border-slate-700 text-slate-400 hover:text-slate-200 hover:bg-slate-800 transition-colors">
          <RefreshCw size={13} />
        </button>
      </div>

      {/* Summary */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <SummaryCard label="Total Deployments" value={analytics.total_deployments} color="text-slate-50" icon={BarChart3} />
        <SummaryCard label="Healthy" value={analytics.healthy} color="text-emerald-400" icon={CheckCircle} />
        <SummaryCard label="Warning" value={analytics.warning} color="text-amber-400" icon={AlertTriangle} />
        <SummaryCard label="Critical" value={analytics.critical} color="text-rose-400" icon={XCircle} />
      </div>

      {/* Restart trend */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
        <div className="flex items-center gap-2 mb-4">
          <TrendingUp size={14} className="text-slate-500" />
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Restart Trend (24h)</h3>
        </div>
        <ResponsiveContainer width="100%" height={140}>
          <BarChart data={analytics.restart_trend} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
            <XAxis dataKey="hour" tick={{ fontSize: 9, fill: "#475569" }} />
            <YAxis tick={{ fontSize: 9, fill: "#475569" }} />
            <Tooltip contentStyle={{ background: "#0f172a", border: "1px solid #1e293b", borderRadius: "6px", fontSize: 11 }} />
            <Bar dataKey="restarts" radius={[2, 2, 0, 0]}>
              {analytics.restart_trend.map((_, i) => (
                <Cell key={i} fill={_.restarts > 3 ? "#f87171" : _.restarts > 1 ? "#fbbf24" : "#6366f1"} />
              ))}
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* CrashLoop detections */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <XCircle size={13} className="text-rose-400" />
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>CrashLoopBackOff</h3>
            {analytics.crash_loop_deployments?.length > 0 && (
              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-rose-400/10 text-rose-400 border border-rose-400/20">{analytics.crash_loop_deployments.length}</span>
            )}
          </div>
          {analytics.crash_loop_deployments?.length === 0 ? (
            <p className="text-xs text-emerald-400 flex items-center gap-1.5"><CheckCircle size={11} />No CrashLoopBackOff detected</p>
          ) : (
            <div className="space-y-2">
              {analytics.crash_loop_deployments.map((dep) => (
                <div key={dep.id} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                  <div>
                    <p className="text-xs font-medium text-slate-200 font-mono">{dep.name}</p>
                    <p className="text-[10px] text-slate-500">{dep.namespace} · {dep.restarts} restarts</p>
                  </div>
                  <span className={cn("text-[10px] px-1.5 py-0.5 rounded border", statusStyle[dep.status])}>{dep.status}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* High restart deployments */}
        <div className="bg-slate-900/50 border border-slate-800 rounded-md p-4 animate-fade-in">
          <div className="flex items-center gap-2 mb-3">
            <Clock size={13} className="text-amber-400" />
            <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>High Restart Count</h3>
          </div>
          {analytics.high_restart_deployments?.length === 0 ? (
            <p className="text-xs text-emerald-400 flex items-center gap-1.5"><CheckCircle size={11} />All restart counts normal</p>
          ) : (
            <div className="space-y-2">
              {analytics.high_restart_deployments.map((dep) => (
                <div key={dep.id} className="flex items-center justify-between py-1.5 border-b border-slate-800/50 last:border-0">
                  <div>
                    <p className="text-xs font-medium text-slate-200 font-mono">{dep.name}</p>
                    <p className="text-[10px] text-slate-500">{dep.namespace}</p>
                  </div>
                  <span className="text-xs font-mono text-amber-400">{dep.restarts} restarts</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Resource efficiency */}
      <div className="bg-slate-900/50 border border-slate-800 rounded-md overflow-hidden animate-fade-in">
        <div className="px-4 py-3 border-b border-slate-800">
          <h3 className="text-sm font-semibold text-slate-200" style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>Deployment Health Scores</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-slate-800">
                {["Deployment", "Namespace", "CPU Req", "Mem Req", "Health Score", "Status"].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left font-semibold uppercase tracking-widest text-slate-500">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {analytics.resource_efficiency?.map((dep) => (
                <tr key={dep.name} className="border-b border-slate-800/40 hover:bg-slate-800/20 transition-colors">
                  <td className="px-4 py-2.5 text-slate-200 font-mono">{dep.name}</td>
                  <td className="px-4 py-2.5 text-slate-400">{dep.namespace}</td>
                  <td className="px-4 py-2.5 text-slate-400 font-mono">{dep.cpu_requested}</td>
                  <td className="px-4 py-2.5 text-slate-400 font-mono">{dep.memory_requested}</td>
                  <td className="px-4 py-2.5">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-1.5 bg-slate-800 rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{
                          width: `${dep.health_score}%`,
                          backgroundColor: dep.health_score >= 90 ? "#34d399" : dep.health_score >= 70 ? "#fbbf24" : "#f87171"
                        }} />
                      </div>
                      <span className="font-mono w-6 text-right" style={{
                        color: dep.health_score >= 90 ? "#34d399" : dep.health_score >= 70 ? "#fbbf24" : "#f87171"
                      }}>{dep.health_score}</span>
                    </div>
                  </td>
                  <td className="px-4 py-2.5">
                    <span className={cn("text-[10px] px-1.5 py-0.5 rounded border",
                      dep.health_score === 100 ? "text-emerald-400 bg-emerald-400/10 border-emerald-400/20" :
                      dep.health_score > 60 ? "text-amber-400 bg-amber-400/10 border-amber-400/20" :
                      "text-rose-400 bg-rose-400/10 border-rose-400/20"
                    )}>{dep.health_score === 100 ? "Healthy" : dep.health_score > 60 ? "Warning" : "Critical"}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
